import { FormGroup, Input, Label, SubmitButton, Switch } from "@xola/ui-kit";
import { FormField, InputField, requiredString } from "common";
import React from "react";
import { Coupon, CouponTypeCopy, Permission } from "types";
import { boolean, object } from "yup";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useAuthorization } from "../../hooks/useAuthorization";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { TagInputField } from "../Field/TagInputField";
import { ContentLayout } from "../Layout/ContentLayout";
import { CouponEditorProps } from "./CouponEditorProps";
import LimitFormField from "./LimitFormField";

const validationSchema = object().shape({
    name: requiredString(),
    excludeFee: boolean(),
});

export const UnlockCouponEditor = ({ coupon, isSaving, onSubmit, onCancel }: CouponEditorProps) => {
    const handleSubmit = async (coupon: Coupon) => {
        onSubmit({ coupon: { ...coupon, limit: Number(coupon?.limit) === 0 ? null : coupon?.limit } });
    };

    const { isGranted } = useAuthorization();

    return (
        <XolaFormik
            validateOnChange
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={coupon}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, setFieldValue }) => (
                <ContentLayout isWide isDisabled={!isGranted(Permission.MODIFY_COUPONS) || isSaving}>
                    <ContentLayout.Section
                        title="Price Unlock Coupon"
                        description="Unlock Coupon allow you to specify an arbitrary price (pre-tax) for a booking"
                    >
                        <FormGroup className="flex-1">
                            <Label>Type</Label>
                            <Input disabled value={CouponTypeCopy.unlock} />
                        </FormGroup>

                        <FormField name="name" label="Campaign Name">
                            <InputField isRequired placeholder="Campaign name" />
                        </FormField>

                        {coupon?.limit && <LimitFormField />}

                        <FormField name="tags" label="Booking Tags">
                            <TagInputField />
                        </FormField>

                        <div>
                            <Switch.Group>
                                <Switch
                                    isChecked={values.excludeTaxFee}
                                    onChange={(value: boolean) => setFieldValue("excludeTaxFee", value)}
                                />

                                <Switch.Label direction="right" className="text-gray-darker">
                                    Remove taxes and fees
                                </Switch.Label>
                            </Switch.Group>

                            <div className="pl-10 text-sm text-gray-dark">
                                If enabled, all taxes and fees will be removed from a booking when a code from this
                                campaign is applied
                            </div>
                        </div>
                    </ContentLayout.Section>

                    <ContentLayout.Footer>
                        <SubmitButton isLoading={isSaving} onClick={handleSubmit}>
                            {coupon?.id ? "Save" : "Next"}
                        </SubmitButton>

                        <SecondaryButton
                            type="reset"
                            onClick={() => {
                                onCancel();
                            }}
                        >
                            Cancel
                        </SecondaryButton>
                    </ContentLayout.Footer>

                    <ScrollToFieldError />
                </ContentLayout>
            )}
        </XolaFormik>
    );
};
